const WORDPRESS_BASE_URL = 'https://blogs.doctorsjobs.io';
const WORDPRESS_API_URL = `${WORDPRESS_BASE_URL}/wp-json/wp/v2`;

// Simple cache manager
class CacheManager {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes
  }

  set(key, data, ttl) {
    if (ttl === undefined) ttl = this.defaultTTL;
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresIn: ttl
    });
  }

  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > entry.expiresIn) {
      this.cache.delete(key);
      return null;
    }
    return entry.data;
  }

  clear() {
    this.cache.clear();
  }

  clearPattern(pattern) {
    const keys = Array.from(this.cache.keys());
    keys.forEach(key => {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    });
  }
}

const cache = new CacheManager();

// Error handling
export class WordPressAPIError extends Error {
  constructor(message, status, response) {
    super(message);
    this.name = 'WordPressAPIError';
    this.status = status;
    this.response = response;
  }
}

// Main WordPress API service
class WordPressService {
  constructor(baseUrl) {
    this.baseUrl = baseUrl || WORDPRESS_API_URL;
  }

  // Generic API request method
  async request(endpoint, params = {}, useCache = true) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          searchParams.append(key, value.join(','));
        } else {
          searchParams.append(key, value.toString());
        }
      }
    });
    const url = `${this.baseUrl}${endpoint}?${searchParams.toString()}`;
    const cacheKey = `wp_api_${url}`;
    // Check cache first
    if (useCache) {
      const cachedData = cache.get(cacheKey);
      if (cachedData) {
        return {
          data: cachedData.data,
          headers: new Headers(cachedData.headers)
        };
      }
    }
    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new WordPressAPIError(
          `WordPress API request failed: ${response.statusText}`,
          response.status,
          response
        );
      }
      const data = await response.json();
      // Cache the response
      if (useCache) {
        const headersObject = {};
        response.headers.forEach((value, key) => {
          headersObject[key] = value;
        });
        cache.set(cacheKey, { data, headers: headersObject });
      }
      return { data, headers: response.headers };
    } catch (error) {
      if (error instanceof WordPressAPIError) {
        throw error;
      }
      throw new WordPressAPIError(
        `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        undefined,
        error
      );
    }
  }

  // Fetch posts with advanced filtering and pagination
  async getPosts(params = {}) {
    const defaultParams = {
      per_page: 12,
      page: 1,
      status: 'publish',
      _embed: true,
      ...params
    };
    const { data: posts, headers } = await this.request('/posts', defaultParams);
    const totalPosts = parseInt(headers.get('X-WP-Total') || '0');
    const totalPages = parseInt(headers.get('X-WP-TotalPages') || '0');
    const currentPage = defaultParams.page || 1;
    return {
      posts,
      totalPosts,
      totalPages,
      currentPage,
      hasMore: currentPage < totalPages
    };
  }

  // Fetch a single post by slug
  async getPostBySlug(slug) {
    try {
      const response = await this.getPosts({ slug, _embed: true });
      return response.posts[0] || null;
    } catch (error) {
      if (error instanceof WordPressAPIError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  // Fetch a single post by ID
  async getPostById(id) {
    try {
      const { data } = await this.request(`/posts/${id}`, { _embed: true });
      return data;
    } catch (error) {
      if (error instanceof WordPressAPIError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  // Fetch categories
  async getCategories(params = {}) {
    const { data } = await this.request('/categories', {
      per_page: 100,
      ...params
    });
    return data;
  }

  // Fetch tags
  async getTags(params = {}) {
    const { data } = await this.request('/tags', {
      per_page: 100,
      ...params
    });
    return data;
  }

  // Search posts
  async searchPosts(query, params = {}) {
    return this.getPosts({ ...params, search: query });
  }

  // Get posts by category
  async getPostsByCategory(categoryId, params = {}) {
    return this.getPosts({ ...params, categories: [categoryId] });
  }

  // Get related posts (by categories)
  async getRelatedPosts(post, limit = 4) {
    if (!post.categories.length) return [];
    const response = await this.getPosts({
      categories: post.categories,
      exclude: [post.id],
      per_page: limit,
      _embed: true
    });
    return response.posts;
  }

  // Bulk fetch posts by slugs
  async getPostsBySlugs(slugs) {
    const posts = [];
    // Fetch in batches to avoid URL length limits
    const batchSize = 10;
    for (let i = 0; i < slugs.length; i += batchSize) {
      const batch = slugs.slice(i, i + batchSize);
      const promises = batch.map(slug => this.getPostBySlug(slug));
      const results = await Promise.all(promises);
      posts.push(...results.filter(Boolean));
    }
    return posts;
  }

  // Clear cache (useful for forced refresh)
  clearCache(pattern) {
    if (pattern) {
      cache.clearPattern(pattern);
    } else {
      cache.clear();
    }
  }

  // Get site info
  async getSiteInfo() {
    const { data } = await this.request('/', {}, true);
    return data;
  }
}

// Export singleton instance
export const wordpressService = new WordPressService();